import { registerStyles } from "../utils/registerStyles";
import React, { useState } from "react";
import LoadingIndicator from "../components/LoadingIndicator";
import { MdBusiness } from "react-icons/md";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";

export default function MyCompaniesPage() {
  const { currentUser } = useAuth();
  const { linkedCompanies, activeCompany, loading, switchCompany } = useCompany();
  const [switchingId, setSwitchingId] = useState(null);
  const sortedCompanies = [...linkedCompanies].sort((first, second) =>
    first.companyName.localeCompare(second.companyName, "sv")
  );

  const isOwnedCompany = (company) =>
    company.companyAccountUid === currentUser?.uid || (
      company.userDocId === company.companyAccountUid &&
      company.employedData?.uid === currentUser?.uid &&
      ["super-admin", "superadmin"].includes(company.role)
    );
  const ownedCompanies = sortedCompanies.filter(isOwnedCompany);
  const otherCompanies = sortedCompanies.filter((company) => !isOwnedCompany(company));
  const renderCompany = (company) => (
    <CompanyCard
      key={company.companyId}
      company={company}
      activeCompany={activeCompany}
      switchingId={switchingId}
      onSelect={selectCompany}
    />
  );

  const selectCompany = async (company) => {
    setSwitchingId(company.id);
    try {
      await switchCompany(company);
    } finally {
      setSwitchingId(null);
    }
  };

  if (loading) {
    return <LoadingIndicator message="Laddar företag..." fullPage />;
  }

  return (
    <div className="my-companies-page">
      <header className="my-companies-header">
        <div>
          <h1 style={{ margin: "0 0 8px", fontSize: 30 }}>Mina företag</h1>
          <p style={{ margin: 0, color: "#69738b" }}>
            Företag kopplade till {currentUser?.email || "din e-postadress"}.
          </p>
        </div>
        <div className="my-companies-active" aria-live="polite">
          <span>Inloggad i:</span>
          <strong>{loading ? "Laddar..." : activeCompany?.companyName || "Inget företag valt"}</strong>
        </div>
      </header>

      <section aria-label="Företagsöversikt" className="my-companies-summary" style={cardStyle}>
        <strong style={{ color: "#6323ef", fontSize: 30 }}>{sortedCompanies.length}</strong>
        <span>Företag kopplade till din e-postadress</span>
      </section>

      {sortedCompanies.length === 0 ? (
        <div style={cardStyle}>Inga aktiva företag hittades för din e-postadress.</div>
      ) : (
        <div className={`my-companies-layout${otherCompanies.length ? "" : " my-companies-layout-single"}`}>
          <section aria-label="Dina företag" style={cardStyle}>
            <h2 style={{ margin: "0 0 16px", fontSize: 18 }}>Dina företag</h2>
            {ownedCompanies.length > 0 ? (
              <div className="my-companies-list">{ownedCompanies.map(renderCompany)}</div>
            ) : (
              <p style={{ margin: 0, color: "#69738b" }}>Inga egna företag är kopplade ännu.</p>
            )}
          </section>
          {otherCompanies.length > 0 && (
            <aside aria-label="Andra företag" style={cardStyle}>
              <h2 style={{ margin: "0 0 6px", fontSize: 18 }}>Andra företag där du är medlem</h2>
              <p style={{ margin: "0 0 16px", color: "#69738b", fontSize: 14 }}>
                Företag där din e-postadress är kopplad till en annan roll, till exempel administratör eller användare.
              </p>
              <div className="my-companies-list">{otherCompanies.map(renderCompany)}</div>
            </aside>
          )}
        </div>
      )}

    </div>
  );
}

function CompanyCard({ company, activeCompany, switchingId, onSelect }) {
  const isActive = company.companyId === activeCompany?.companyId;

  return (
    <div style={{
      ...cardStyle,
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 16,
    }}>
      <div style={{
        background: "#f4f1ff",
        borderRadius: 12,
        padding: 12,
        color: "#6323ef",
        display: "flex",
      }}>
        <MdBusiness size={24} aria-hidden="true" />
      </div>
      <div style={{ flex: 1, minWidth: 150 }}>
        <div style={{ fontSize: 18, fontWeight: 700, overflowWrap: "anywhere" }}>{company.companyName}</div>
        <div style={{ marginTop: 4, color: "#69738b" }}>{company.roleLabel}</div>
      </div>
      {isActive && (
        <span style={{ color: "#6323ef", background: "#f4f1ff", borderRadius: 20, padding: "7px 12px", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
          Aktuellt företag
        </span>
      )}
      {!isActive && company.role === "super-admin" && (
        <button
          type="button"
          disabled={switchingId !== null}
          onClick={() => onSelect(company)}
          style={{ border: 0, borderRadius: 10, background: "#6323ef", color: "#fff", padding: "10px 14px", fontWeight: 600, cursor: "pointer" }}
        >
          {switchingId === company.id ? "Växlar..." : "Välj företag"}
        </button>
      )}
    </div>
  );
}

const cardStyle = {
  background: "#fff",
  border: "1px solid #ececf4",
  borderRadius: 16,
  padding: 20,
};

// Styles live with this component; keep selectors and responsive rules together.
registerStyles("MyCompaniesPage", `
.my-companies-page {
  width: 100%;
  max-width: 1600px;
  margin: 0;
  color: #252b42;
}

.my-companies-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px 24px;
  margin-bottom: 28px;
}

.my-companies-active {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 100%;
  padding: 12px 16px;
  border: 1px solid #ded4ff;
  border-radius: 12px;
  background: #f7f4ff;
  color: #69738b;
  font-size: 13px;
}

.my-companies-active strong {
  color: #252b42;
  font-size: 15px;
  overflow-wrap: anywhere;
}

.my-companies-summary {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.my-companies-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(320px, 0.85fr);
  align-items: start;
  gap: 20px;
}

.my-companies-layout-single {
  grid-template-columns: minmax(0, 1fr);
}

.my-companies-layout > * {
  min-width: 0;
}

@media (max-width: 960px) {
  .my-companies-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

.my-companies-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 420px), 1fr));
  gap: 16px;
  padding-bottom: 24px;
}
`);
